/**
 * OnboardingPage — multi-step welcome wizard for new users.
 * Shown on first login; stores completion in localStorage.
 */
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronRight, ChevronLeft, Sparkles, Target, MessageSquare,
  Link2, GraduationCap, BarChart2, BookOpen, Bot, FlaskConical, Search,
  TrendingUp, DollarSign, LineChart, ArrowUpRight, CheckCircle,
} from 'lucide-react'
import { api } from '@/lib/api'

const STORAGE_KEY = 'av-onboarding-complete'

// ── Step 1: Experience Level ───────────────────────────────────────────────

const LEVELS = [
  {
    id: 'beginner',
    label: 'Beginner',
    desc: 'New to trading',
    icon: GraduationCap,
    color: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
    selected: 'from-emerald-500/30 to-emerald-600/20 border-emerald-400 ring-2 ring-emerald-400/40',
  },
  {
    id: 'intermediate',
    label: 'Intermediate',
    desc: 'Some experience with trading strategies',
    icon: BarChart2,
    color: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    selected: 'from-blue-500/30 to-blue-600/20 border-blue-400 ring-2 ring-blue-400/40',
  },
  {
    id: 'expert',
    label: 'Expert',
    desc: 'Experienced algorithmic trader',
    icon: Bot,
    color: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    selected: 'from-purple-500/30 to-purple-600/20 border-purple-400 ring-2 ring-purple-400/40',
  },
] as const

function StepExperience({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-text-primary">
          What's your experience level?
        </h2>
        <p className="text-text-muted text-sm">
          We'll tailor the experience to match your expertise.
        </p>
      </div>
      <div className="grid gap-4 max-w-md mx-auto">
        {LEVELS.map((l) => {
          const active = value === l.id
          return (
            <button
              key={l.id}
              onClick={() => onChange(l.id)}
              className={`flex items-center gap-4 p-5 rounded-xl border bg-gradient-to-br transition-all duration-200 text-left ${
                active ? l.selected : l.color + ' hover:scale-[1.02]'
              }`}
            >
              <div className="w-11 h-11 rounded-lg bg-text-primary/10 flex items-center justify-center shrink-0">
                <l.icon size={22} className="text-text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-text-primary">{l.label}</p>
                <p className="text-xs text-text-muted mt-0.5">{l.desc}</p>
              </div>
              {active && <CheckCircle size={20} className="text-emerald-400 shrink-0" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step 2: Goals ──────────────────────────────────────────────────────────

const GOAL_OPTIONS = [
  { id: 'trading', label: 'Trading', icon: TrendingUp },
  { id: 'investing', label: 'Investing', icon: DollarSign },
  { id: 'learning', label: 'Learning', icon: BookOpen },
  { id: 'automation', label: 'Automation', icon: Bot },
  { id: 'backtesting', label: 'Backtesting', icon: FlaskConical },
  { id: 'research', label: 'Research', icon: Search },
]

function StepGoals({
  value,
  onChange,
}: {
  value: string[]
  onChange: (v: string[]) => void
}) {
  const toggle = (id: string) => {
    onChange(
      value.includes(id) ? value.filter((g) => g !== id) : [...value, id],
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-text-primary">
          What are your goals?
        </h2>
        <p className="text-text-muted text-sm">
          Select all that apply. You can change these later.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-lg mx-auto">
        {GOAL_OPTIONS.map((g) => {
          const active = value.includes(g.id)
          return (
            <button
              key={g.id}
              onClick={() => toggle(g.id)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200 ${
                active
                  ? 'bg-blue/20 border-blue ring-2 ring-blue/30 text-text-primary'
                  : 'bg-surface-card border-border hover:border-blue/40 text-text-muted hover:text-text-primary'
              }`}
            >
              <g.icon size={24} />
              <span className="text-sm font-medium">{g.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step 3: Build Your First Strategy ──────────────────────────────────────

const STRATEGY_SUGGESTIONS = [
  { id: 'momentum', label: 'Momentum stocks', icon: ArrowUpRight },
  { id: 'value', label: 'Value investing', icon: DollarSign },
  { id: 'index', label: 'Index tracking', icon: LineChart },
  { id: 'dividend', label: 'Dividend growth', icon: TrendingUp },
]

function StepStrategy({
  value,
  onChange,
}: {
  value: string | null
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-text-primary">
          Build your first strategy
        </h2>
      </div>

      {/* Chat-like ARIA message */}
      <div className="max-w-md mx-auto">
        <div className="flex items-start gap-3 p-4 rounded-xl bg-surface-card border border-border">
          <div className="w-9 h-9 rounded-full bg-grad-blue flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-white" />
          </div>
          <div className="space-y-1 flex-1">
            <p className="text-xs font-semibold text-blue">ARIA</p>
            <p className="text-sm text-text-primary">
              I'll help you build your first strategy. What are you interested in?
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          {STRATEGY_SUGGESTIONS.map((s) => {
            const active = value === s.id
            return (
              <button
                key={s.id}
                onClick={() => onChange(s.id)}
                className={`flex items-center gap-2.5 p-3.5 rounded-xl border transition-all duration-200 text-left ${
                  active
                    ? 'bg-blue/20 border-blue ring-2 ring-blue/30'
                    : 'bg-surface-card border-border hover:border-blue/40'
                }`}
              >
                <s.icon size={18} className={active ? 'text-blue' : 'text-text-muted'} />
                <span className={`text-sm font-medium ${active ? 'text-text-primary' : 'text-text-muted'}`}>
                  {s.label}
                </span>
              </button>
            )
          })}
        </div>

        {value && (
          <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
            <p className="text-xs text-emerald-400">
              <CheckCircle size={14} className="inline mr-1 -mt-0.5" />
              A backtest for <span className="font-semibold">{STRATEGY_SUGGESTIONS.find(s => s.id === value)?.label}</span> will
              be created when you finish onboarding.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Step 4: Connect Broker ─────────────────────────────────────────────────

const BROKERS = [
  {
    id: 'alpaca',
    label: 'Alpaca',
    desc: 'Free paper trading account',
    badge: 'Recommended',
  },
  {
    id: 'ibkr',
    label: 'Interactive Brokers',
    desc: 'Full brokerage with live trading',
    badge: null,
  },
  {
    id: 'skip',
    label: 'Skip for now',
    desc: 'You can connect a broker later in Settings',
    badge: null,
  },
]

function StepBroker({
  value,
  onChange,
}: {
  value: string | null
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-text-primary">
          Connect a broker
        </h2>
        <p className="text-text-muted text-sm">
          Link your brokerage account to enable live or paper trading.
        </p>
      </div>
      <div className="grid gap-3 max-w-md mx-auto">
        {BROKERS.map((b) => {
          const active = value === b.id
          return (
            <button
              key={b.id}
              onClick={() => onChange(b.id)}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left ${
                active
                  ? 'bg-blue/20 border-blue ring-2 ring-blue/30'
                  : 'bg-surface-card border-border hover:border-blue/40'
              }`}
            >
              <Link2
                size={20}
                className={active ? 'text-blue' : 'text-text-muted'}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-text-primary text-sm">{b.label}</p>
                  {b.badge && (
                    <span className="text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                      {b.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-0.5">{b.desc}</p>
              </div>
              {active && <CheckCircle size={18} className="text-blue shrink-0" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Main Wizard ────────────────────────────────────────────────────────────

const STEP_LABELS = ['Experience', 'Goals', 'Strategy', 'Broker']

export default function OnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)

  // State
  const [experience, setExperience] = useState('')
  const [goals, setGoals] = useState<string[]>([])
  const [strategy, setStrategy] = useState<string | null>(null)
  const [broker, setBroker] = useState<string | null>(null)

  const canNext =
    (step === 0 && experience !== '') ||
    (step === 1 && goals.length > 0) ||
    step === 2 ||
    step === 3

  const handleFinish = useCallback(async () => {
    setSaving(true)
    try {
      await api.post('/api/onboarding/complete', {
        experience_level: experience,
        goals,
        first_strategy: strategy,
        broker: broker ?? 'skip',
      })
    } catch (e) {
      console.warn('Failed to save onboarding prefs:', e)
    }
    localStorage.setItem(STORAGE_KEY, 'true')
    setSaving(false)
    navigate('/app', { replace: true })
  }, [experience, goals, strategy, broker, navigate])

  const next = () => {
    if (step < 3) setStep(step + 1)
    else handleFinish()
  }

  return (
    <div className="min-h-screen bg-base flex flex-col items-center justify-center p-6">
      {/* Progress bar */}
      <div className="w-full max-w-md mb-8">
        <div className="flex items-center justify-between mb-2">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex items-center gap-1.5">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i <= step
                    ? 'bg-blue text-white'
                    : 'bg-surface-card text-text-muted border border-border'
                }`}
              >
                {i + 1}
              </div>
              <span
                className={`text-xs hidden sm:inline ${
                  i <= step ? 'text-text-primary font-medium' : 'text-text-muted'
                }`}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
        <div className="h-1 bg-surface-card rounded-full overflow-hidden">
          <div
            className="h-full bg-blue rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div className="card w-full max-w-lg p-6 sm:p-8">
        <p className="panel-title text-center mb-5">
          Step {step + 1} of 4 · {STEP_LABELS[step]}
        </p>
        {step === 0 && (
          <StepExperience value={experience} onChange={setExperience} />
        )}
        {step === 1 && <StepGoals value={goals} onChange={setGoals} />}
        {step === 2 && (
          <StepStrategy value={strategy} onChange={setStrategy} />
        )}
        {step === 3 && <StepBroker value={broker} onChange={setBroker} />}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          {step > 0 ? (
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              <ChevronLeft size={16} /> Back
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={next}
            disabled={!canNext || saving}
            className="btn-primary gap-1.5 px-5 py-2.5"
          >
            {saving ? (
              'Saving...'
            ) : step < 3 ? (
              <>
                Next <ChevronRight size={16} />
              </>
            ) : (
              <>
                Get Started <Sparkles size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Skip onboarding */}
      <button
        onClick={() => {
          localStorage.setItem(STORAGE_KEY, 'true')
          navigate('/app', { replace: true })
        }}
        className="mt-4 text-xs text-text-muted hover:text-text-primary transition-colors"
      >
        Skip onboarding
      </button>
    </div>
  )
}
